import Graphic from "@arcgis/core/Graphic";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import VersionManagementService from "@arcgis/core/versionManagement/VersionManagementService";
import { AppConfig } from "../AppConfig";
import { isOperativeLayerId } from "../constants/NauticalObjects";
import { applyEdits, getAllRelatedFeatures, getMapView } from "./arcgisUtils";
import { getAGEToken } from "./authenticate";
import { toast } from "react-toastify";
import {
    FeatureObject,
    getAllRelatedFeaturesRecursive,
    getDifferencesObjectIds,
    getSessionID,
} from "./newVersioningUtils";
import type PortalUser from "@arcgis/core/portal/PortalUser";
import type { VersionInfoExtended, VersionInfo } from "@arcgis/core/versionManagement/support/jsonTypes";
import type FeatureSet from "@arcgis/core/rest/support/FeatureSet";

const getVersionServiceUrl = () => {
    return AppConfig.ArcGIS.BaseUrl + AppConfig.ArcGIS.VersionManagementService;
};

export const getDefaultAndOperativeFeature = async (feature: Graphic, onlyDefault: boolean = false) => {
    const layerId = (feature.layer as FeatureLayer).layerId;
    if (layerId === undefined || layerId === null) {
        return { defaultFeature: null, operativeFeature: null };
    }

    const defaultFeatureLayer = new FeatureLayer({
        url: `${AppConfig.NauticalObjectsFeatureServer}${layerId}`,
    });

    const defaultFeatureQuery = defaultFeatureLayer.createQuery();
    defaultFeatureQuery.objectIds = [feature.attributes.objectid];
    defaultFeatureQuery.outFields = ["*"];
    defaultFeatureQuery.returnGeometry = true;
    let defaultFeatureResult: FeatureSet;
    try {
        defaultFeatureResult = await defaultFeatureLayer.queryFeatures(defaultFeatureQuery);
    } catch (error) {
        console.error("Error querying default feature: ", error);
        toast.error("Feil ved henting av objekt fra Planroot (default)");
        throw new Error("Failed to query default feature");
    }

    // The operative feature server does not have all the layers of the editing feature
    // server, so a layer that is missing there has no operative feature to fetch.
    if (onlyDefault || !isOperativeLayerId(layerId)) {
        return {
            defaultFeature: defaultFeatureResult.features.length > 0 ? defaultFeatureResult.features[0] : null,
            operativeFeature: null,
        };
    }

    const operativeFeatureLayer = new FeatureLayer({
        url: `${AppConfig.OperativeObjectsFeatureServer}${layerId}`,
    });

    const operativeFeatureQuery = operativeFeatureLayer.createQuery();
    operativeFeatureQuery.where = `globalid = '${feature.attributes.globalid}'`;
    operativeFeatureQuery.outFields = ["*"];
    operativeFeatureQuery.returnGeometry = true;
    let operativeFeatureResult: FeatureSet;
    try {
        operativeFeatureResult = await operativeFeatureLayer.queryFeatures(operativeFeatureQuery);
    } catch (error) {
        console.error("Error querying operative feature: ", error);
        toast.error("Feil ved henting av objekt fra operativ (operative)");
        throw new Error("Failed to query operative feature");
    }

    const result = {
        defaultFeature: defaultFeatureResult.features.length > 0 ? defaultFeatureResult.features[0] : null,
        operativeFeature: operativeFeatureResult.features.length > 0 ? operativeFeatureResult.features[0] : null,
    };
    return result;
};

export const restoreAttributes = async (
    restoreFromFeature: Graphic,
    restoreToFeature: Graphic,
    runApplyEdit: boolean
): Promise<Graphic | null> => {
    const newFeature = restoreToFeature.clone();

    const layer = restoreToFeature.layer;

    if (!layer) {
        console.error("Layer not found for feature to restore attributes to");
        return null;
    }

    newFeature.layer = layer;
    newFeature.attributes = restoreFromFeature.attributes;
    newFeature.attributes.objectid = restoreToFeature.attributes.objectid;

    if (runApplyEdit) {
        const result = await applyEdits(layer as FeatureLayer, { updateFeatures: [newFeature] });
        if (result.updateFeatureResults[0].error) {
            console.error("Error updating feature: ", result.updateFeatureResults[0].error);
            return null;
        } else {
            return newFeature;
        }
    } else {
        return newFeature;
    }
};

export const restoreGeometry = async (
    restoreFromFeature: Graphic,
    restoreToFeature: Graphic,
    runApplyEdit: boolean
): Promise<Graphic | null> => {
    const newFeature = restoreToFeature.clone();
    const layer = restoreToFeature.layer;

    if (!layer) {
        return null;
    }

    newFeature.geometry = restoreFromFeature.geometry;
    newFeature.layer = layer;

    if (runApplyEdit) {
        const result = await applyEdits(layer as FeatureLayer, { updateFeatures: [newFeature] });
        if (result.updateFeatureResults[0].error) {
            console.error("Error updating feature: ", result.updateFeatureResults[0].error);
            return null;
        } else {
            return newFeature;
        }
    } else {
        return newFeature;
    }
};

export const restoreAllFromFeatureObject = async (
    restoreFromFeature: Graphic,
    restoreToFeature: Graphic
): Promise<Graphic | null> => {
    const restoredAttributesFeature = await restoreAttributes(restoreFromFeature, restoreToFeature, false);
    if (!restoredAttributesFeature) {
        return null;
    }
    const restoredFullyFeature = await restoreGeometry(restoreFromFeature, restoredAttributesFeature, false);
    const layer = restoredFullyFeature?.layer;

    if (!layer || layer.type !== "feature") {
        return restoredFullyFeature;
    }

    await applyEdits(layer as FeatureLayer, { updateFeatures: [restoredFullyFeature] });

    return restoredFullyFeature;
};

export const restoreNauticalObjectFully = async (
    restoreFromFeature: Graphic,
    restoreToFeature: Graphic,
    fromOperative: boolean
): Promise<Graphic | null> => {
    // restore main feature
    const restoredMainFeature = await restoreAllFromFeatureObject(restoreFromFeature, restoreToFeature);
    const mapView = getMapView();
    if (!mapView?.map) {
        console.error("MapView or map not found");
        return restoredMainFeature;
    }
    await restoreSectorData(
        restoreToFeature,
        fromOperative ? undefined : restoreFromFeature,
        fromOperative ? restoreFromFeature : undefined
    );
    if (fromOperative) {
        // Get related version features since operative features do not have relationships
        const getRelatedFeaturesResults = await getAllRelatedFeatures(restoreToFeature);
        // find ib, racon, flytendemerke features
        const relatedIBFeatures = getRelatedFeaturesResults.find((feature) => {
            return feature.relationship.name === "IB";
        });
        const relatedRaconFeatures = getRelatedFeaturesResults.find((feature) => {
            return feature.relationship.name === "Racon";
        });
        const relatedFlytendemerkeFeatures = getRelatedFeaturesResults.find((feature) => {
            return feature.relationship.name === "FlytendeMerke";
        });

        const { IB, Racon, Floating } = await getLightRelatedOperativeFeatures(
            relatedIBFeatures?.features[0],
            relatedRaconFeatures?.features[0],
            relatedFlytendemerkeFeatures?.features[0]
        );

        if (IB && relatedIBFeatures && relatedIBFeatures.features.length > 0) {
            const ibversionFeature = relatedIBFeatures.features[0];
            const versionIbLayer = mapView.map.findLayerById("IB") as FeatureLayer;
            ibversionFeature.layer = versionIbLayer;
            const restoredIBFeature = await restoreAllFromFeatureObject(IB, ibversionFeature);
            if (!restoredIBFeature) {
                console.error("Error restoring related IB feature");
                toast.error("Feil ved gjenoppretting av relatert IB");
            }
        }
        if (Racon && relatedRaconFeatures && relatedRaconFeatures.features.length > 0) {
            const raconversionFeature = relatedRaconFeatures.features[0];
            const versionRaconLayer = mapView.map.findLayerById("Racon") as FeatureLayer;
            raconversionFeature.layer = versionRaconLayer;
            const restoredRaconFeature = await restoreAllFromFeatureObject(Racon, raconversionFeature);
            if (!restoredRaconFeature) {
                console.error("Error restoring related Racon feature");
                toast.error("Feil ved gjenoppretting av relatert Racon");
            }
        }
        if (Floating && relatedFlytendemerkeFeatures && relatedFlytendemerkeFeatures.features.length > 0) {
            const floatingVersionFeature = relatedFlytendemerkeFeatures.features[0];
            const versionFloatingLayer = mapView.map.findLayerById("Flytende merke") as FeatureLayer;
            floatingVersionFeature.layer = versionFloatingLayer;
            const restoredFloatingFeature = await restoreAllFromFeatureObject(Floating, floatingVersionFeature);
            if (!restoredFloatingFeature) {
                console.error("Error restoring related FlytendeMerke feature");
                toast.error("Feil ved gjenoppretting av relatert FlytendeMerke");
            }
        }
    } else {
        // Get related features from default feature
        const getRelatedFeaturesResults = await getAllRelatedFeatures(restoreFromFeature);
        const getRelatedFeaturesResultsVersion = await getAllRelatedFeatures(restoreToFeature);
        // find ib, racon, flytendemerke features
        const relatedIBFeatures = getRelatedFeaturesResults.find((feature) => {
            return feature.relationship.name === "IB";
        });
        const relatedRaconFeatures = getRelatedFeaturesResults.find((feature) => {
            return feature.relationship.name === "Racon";
        });
        const relatedFlytendemerkeFeatures = getRelatedFeaturesResults.find((feature) => {
            return feature.relationship.name === "FlytendeMerke";
        });
        const relatedIBFeaturesVersion = getRelatedFeaturesResultsVersion.find((feature) => {
            return feature.relationship.name === "IB";
        });
        const relatedRaconFeaturesVersion = getRelatedFeaturesResultsVersion.find((feature) => {
            return feature.relationship.name === "Racon";
        });
        const relatedFlytendemerkeFeaturesVersion = getRelatedFeaturesResultsVersion.find((feature) => {
            return feature.relationship.name === "FlytendeMerke";
        });
        if (
            relatedIBFeatures &&
            relatedIBFeatures.features.length > 0 &&
            relatedIBFeaturesVersion &&
            relatedIBFeaturesVersion.features.length > 0
        ) {
            const ibversionFeature = relatedIBFeatures.features[0];
            const versionIbLayer = mapView.map.findLayerById("IB") as FeatureLayer;
            ibversionFeature.layer = versionIbLayer;
            const restoredIBFeature = await restoreAllFromFeatureObject(
                relatedIBFeatures.features[0],
                ibversionFeature
            );
            if (!restoredIBFeature) {
                console.error("Error restoring related IB feature");
                toast.error("Feil ved gjenoppretting av relatert IB");
            }
        }
        if (
            relatedRaconFeatures &&
            relatedRaconFeatures.features.length > 0 &&
            relatedRaconFeaturesVersion &&
            relatedRaconFeaturesVersion.features.length > 0
        ) {
            const raconversionFeature = relatedRaconFeatures.features[0];
            const versionRaconLayer = mapView.map.findLayerById("Racon") as FeatureLayer;
            raconversionFeature.layer = versionRaconLayer;
            const restoredRaconFeature = await restoreAllFromFeatureObject(
                relatedRaconFeatures.features[0],
                raconversionFeature
            );
            if (!restoredRaconFeature) {
                console.error("Error restoring related Racon feature");
                toast.error("Feil ved gjenoppretting av relatert Racon");
            }
        }
        if (
            relatedFlytendemerkeFeatures &&
            relatedFlytendemerkeFeatures.features.length > 0 &&
            relatedFlytendemerkeFeaturesVersion &&
            relatedFlytendemerkeFeaturesVersion.features.length > 0
        ) {
            const floatingVersionFeature = relatedFlytendemerkeFeatures.features[0];
            const versionFloatingLayer = mapView.map.findLayerById("Flytende merke") as FeatureLayer;
            floatingVersionFeature.layer = versionFloatingLayer;
            const restoredFloatingFeature = await restoreAllFromFeatureObject(
                relatedFlytendemerkeFeatures.features[0],
                floatingVersionFeature
            );
            if (!restoredFloatingFeature) {
                console.error("Error restoring related FlytendeMerke feature");
                toast.error("Feil ved gjenoppretting av relatert FlytendeMerke");
            }
        }
    }

    return restoredMainFeature;
};

/**
 * Should only send either defaultLighthouseFeature or operativeLighthouseFeature, not both
 * @param versionLighthouseFeature
 * @param defaultLighthouseFeature
 * @param operativeLighthouseFeature
 * @returns
 */
export const restoreSectorData = async (
    versionLighthouseFeature: Graphic,
    defaultLighthouseFeature?: Graphic,
    operativeLighthouseFeature?: Graphic
) => {
    if ((versionLighthouseFeature.layer as FeatureLayer).layerId !== 0) {
        // Not a light feature
        return;
    }
    const { featuresToAdd, featuresToDelete } = await getAllLightHouseFeatures(
        versionLighthouseFeature,
        defaultLighthouseFeature,
        operativeLighthouseFeature
    );
    const mapView = getMapView();
    if (!mapView?.map) {
        return;
    }
    const lightSectorLayer = mapView.map.findTableById("Lyssektor") as FeatureLayer;
    const sectorLineLayer = mapView.map.findLayerById("Sektorlinje") as FeatureLayer;
    const sectorAreaLayer = mapView.map.findLayerById("Sektorflate") as FeatureLayer;

    const lightsectorUpdates = [];
    const sectorlineUpdates = [];
    const sectorareaUpdates = [];

    // Check if the same globalid exists in both add and delete lists for lightsectors
    // If so, we need to treat it as an update instead
    for (const featureToAdd of featuresToAdd.lightSector) {
        const matchingDelete = featuresToDelete.lightSector.find(
            (featureToDelete) => featureToDelete.attributes.globalid === featureToAdd.attributes.globalid
        );
        if (matchingDelete) {
            lightsectorUpdates.push(featureToAdd);
            // Remove from add list
            featuresToAdd.lightSector = featuresToAdd.lightSector.filter(
                (feature) => feature.attributes.globalid !== featureToAdd.attributes.globalid
            );
            // Remove from delete list
            featuresToDelete.lightSector = featuresToDelete.lightSector.filter(
                (featureToDelete) => featureToDelete.attributes.globalid !== featureToAdd.attributes.globalid
            );
        }
    }

    for (const featureToAdd of featuresToAdd.sectorLine) {
        const matchingDelete = featuresToDelete.sectorLine.find(
            (featureToDelete) => featureToDelete.attributes.globalid === featureToAdd.attributes.globalid
        );
        if (matchingDelete) {
            sectorlineUpdates.push(featureToAdd);
            // Remove from add list
            featuresToAdd.sectorLine = featuresToAdd.sectorLine.filter(
                (feature) => feature.attributes.globalid !== featureToAdd.attributes.globalid
            );
            // Remove from delete list
            featuresToDelete.sectorLine = featuresToDelete.sectorLine.filter(
                (featureToDelete) => featureToDelete.attributes.globalid !== featureToAdd.attributes.globalid
            );
        }
    }

    for (const featureToAdd of featuresToAdd.sectorArea) {
        const matchingDelete = featuresToDelete.sectorArea.find(
            (featureToDelete) => featureToDelete.attributes.globalid === featureToAdd.attributes.globalid
        );
        if (matchingDelete) {
            sectorareaUpdates.push(featureToAdd);
            // Remove from add list
            featuresToAdd.sectorArea = featuresToAdd.sectorArea.filter(
                (feature) => feature.attributes.globalid !== featureToAdd.attributes.globalid
            );
            // Remove from delete list
            featuresToDelete.sectorArea = featuresToDelete.sectorArea.filter(
                (featureToDelete) => featureToDelete.attributes.globalid !== featureToAdd.attributes.globalid
            );
        }
    }

    const lightSectorResult = await lightSectorLayer.applyEdits(
        {
            addFeatures: featuresToAdd.lightSector,
            updateFeatures: lightsectorUpdates,
            deleteFeatures: featuresToDelete.lightSector,
        },
        { globalIdUsed: true }
    );
    const sectorLineResult = await sectorLineLayer.applyEdits(
        {
            addFeatures: featuresToAdd.sectorLine,
            updateFeatures: sectorlineUpdates,
            deleteFeatures: featuresToDelete.sectorLine,
        },
        { globalIdUsed: true }
    );
    const sectorAreaResult = await sectorAreaLayer.applyEdits(
        {
            addFeatures: featuresToAdd.sectorArea,
            updateFeatures: sectorareaUpdates,
            deleteFeatures: featuresToDelete.sectorArea,
        },
        { globalIdUsed: true }
    );

    if (
        lightSectorResult.addFeatureResults.some((r) => r.error) ||
        lightSectorResult.updateFeatureResults.some((r) => r.error) ||
        lightSectorResult.deleteFeatureResults.some((r) => r.error) ||
        sectorLineResult.addFeatureResults.some((r) => r.error) ||
        sectorLineResult.updateFeatureResults.some((r) => r.error) ||
        sectorLineResult.deleteFeatureResults.some((r) => r.error) ||
        sectorAreaResult.addFeatureResults.some((r) => r.error) ||
        sectorAreaResult.updateFeatureResults.some((r) => r.error) ||
        sectorAreaResult.deleteFeatureResults.some((r) => r.error)
    ) {
        console.error("Error restoring sector data");
        toast.error("Feil ved gjenoppretting av sektordata");
    } else {
        toast.success("Sektordata gjenopprettet");
    }
};

export const getAllLightHouseFeatures = async (
    restoreToLighthouseFeature: Graphic,
    restoreFromLighthouseFeatureDefault?: Graphic,
    restoreFromLighthouseFeatureOperative?: Graphic
): Promise<{
    featuresToAdd: { lightSector: Graphic[]; sectorLine: Graphic[]; sectorArea: Graphic[] };
    featuresToDelete: { lightSector: Graphic[]; sectorLine: Graphic[]; sectorArea: Graphic[] };
}> => {
    const lightSectorVersionFeatures: Graphic[] = [];
    const lightSectorDefaultFeatures: Graphic[] = [];
    const lightSectorOperativeFeatures: Graphic[] = [];

    const sectorlineVersionFeatures: Graphic[] = [];
    const sectorlineDefaultFeatures: Graphic[] = [];
    const sectorlineOperativeFeatures: Graphic[] = [];

    const sectorAreaVersionFeatures: Graphic[] = [];
    const sectorAreaDefaultFeatures: Graphic[] = [];
    const sectorAreaOperativeFeatures: Graphic[] = [];

    const firstAllRelated = await getAllRelatedFeatures(restoreToLighthouseFeature);
    const lightSectorFeatures = firstAllRelated.find((feature) => {
        return feature.relationship.name === "Lyssektor";
    });
    if (lightSectorFeatures?.features && lightSectorFeatures.features.length > 0) {
        lightSectorVersionFeatures.push(...lightSectorFeatures.features);
    }

    const lightSectorFeatureObjectIdsVersion = lightSectorFeatures?.features.map(
        (feature) => feature.attributes.objectid
    );
    if (lightSectorFeatureObjectIdsVersion && lightSectorFeatureObjectIdsVersion.length > 0) {
        const mapView = getMapView();
        if (!mapView?.map) {
            return {
                featuresToAdd: { lightSector: [], sectorLine: [], sectorArea: [] },
                featuresToDelete: { lightSector: [], sectorLine: [], sectorArea: [] },
            };
        }
        const lightSectorLayer = mapView.map.findTableById("Lyssektor") as FeatureLayer;
        const relationshipId = lightSectorLayer.relationships?.find((rel) => rel.name === "Lyssektor_L")?.id;
        const lightSectorLinesResult = await lightSectorLayer.queryRelatedFeatures({
            outFields: ["*"],
            relationshipId: relationshipId,
            objectIds: lightSectorFeatureObjectIdsVersion,
            returnGeometry: true,
        });
        const relationshipIdArea = lightSectorLayer.relationships?.find((rel) => rel.name === "Lyssektor_F")?.id;
        const lightSectorAreaResult = await lightSectorLayer.queryRelatedFeatures({
            outFields: ["*"],
            relationshipId: relationshipIdArea,
            objectIds: lightSectorFeatureObjectIdsVersion,
            returnGeometry: true,
        });
        for (const key in lightSectorLinesResult) {
            const relatedLines = lightSectorLinesResult[key]?.features;
            if (relatedLines && relatedLines.length > 0) {
                sectorlineVersionFeatures.push(...relatedLines);
            }
        }
        for (const key in lightSectorAreaResult) {
            const relatedAreas = lightSectorAreaResult[key]?.features;
            if (relatedAreas && relatedAreas.length > 0) {
                sectorAreaVersionFeatures.push(...relatedAreas);
            }
        }
    }

    if (restoreFromLighthouseFeatureOperative) {
        const lightSectorLayer = new FeatureLayer({
            url: `${AppConfig.OperativeObjectsFeatureServer}19`,
        });

        const lightSectorResults = await lightSectorLayer.queryFeatures({
            returnGeometry: true,
            outFields: ["*"],
            where: `fknavinst = '${restoreFromLighthouseFeatureOperative.attributes.globalid}' AND nfs_status = 20`,
        });
        const lightSectorFeatureGlobalIds = lightSectorResults.features.map((feature) => feature.attributes.globalid);
        if (lightSectorFeatureGlobalIds && lightSectorFeatureGlobalIds.length > 0) {
            const sectorLineLayer = new FeatureLayer({
                url: `${AppConfig.OperativeObjectsFeatureServer}2`,
            });
            const sectorLinesResult = await sectorLineLayer.queryFeatures({
                where: `fksektorid IN ('${lightSectorFeatureGlobalIds.join("','")}')`,
                returnGeometry: true,
                outFields: ["*"],
            });
            const sectorAreaLayer = new FeatureLayer({
                url: `${AppConfig.OperativeObjectsFeatureServer}3`,
            });
            const sectorAreasResult = await sectorAreaLayer.queryFeatures({
                where: `fksektorid IN ('${lightSectorFeatureGlobalIds.join("','")}')`,
                returnGeometry: true,
                outFields: ["*"],
            });

            if (sectorLinesResult.features && sectorLinesResult.features.length > 0) {
                sectorlineOperativeFeatures.push(...sectorLinesResult.features);
            }
            if (sectorAreasResult.features && sectorAreasResult.features.length > 0) {
                sectorAreaOperativeFeatures.push(...sectorAreasResult.features);
            }
            if (lightSectorResults?.features && lightSectorResults.features.length > 0) {
                lightSectorOperativeFeatures.push(...lightSectorResults.features);
            }
        }
        return {
            featuresToAdd: {
                lightSector: lightSectorOperativeFeatures,
                sectorLine: sectorlineOperativeFeatures,
                sectorArea: sectorAreaOperativeFeatures,
            },
            featuresToDelete: {
                lightSector: lightSectorVersionFeatures,
                sectorLine: sectorlineVersionFeatures,
                sectorArea: sectorAreaVersionFeatures,
            },
        };
    } else if (restoreFromLighthouseFeatureDefault) {
        const firstAllRelatedDefault = await getAllRelatedFeatures(restoreFromLighthouseFeatureDefault);
        const lightSectorFeatures = firstAllRelatedDefault.find((feature) => {
            return feature.relationship.name === "Lyssektor";
        });
        const lightSectorFeatureObjectIds = lightSectorFeatures?.features.map((feature) => feature.attributes.objectid);
        if (lightSectorFeatureObjectIds && lightSectorFeatureObjectIds.length > 0) {
            const mapView = getMapView();
            if (!mapView?.map) {
                return {
                    featuresToAdd: { lightSector: [], sectorLine: [], sectorArea: [] },
                    featuresToDelete: { lightSector: [], sectorLine: [], sectorArea: [] },
                };
            }
            const lightSectorLayer = mapView.map.findTableById("Lyssektor") as FeatureLayer;
            const relationshipId = lightSectorLayer.relationships?.find((rel) => rel.name === "Lyssektor_L")?.id;
            const lightSectorLinesResult = await lightSectorLayer.queryRelatedFeatures({
                outFields: ["*"],
                relationshipId: relationshipId,
                objectIds: lightSectorFeatureObjectIds,
                returnGeometry: true,
                gdbVersion: "SDE.DEFAULT",
            });

            const relationshipIdArea = lightSectorLayer.relationships?.find((rel) => rel.name === "Lyssektor_F")?.id;
            const lightSectorAreaResult = await lightSectorLayer.queryRelatedFeatures({
                outFields: ["*"],
                relationshipId: relationshipIdArea,
                objectIds: lightSectorFeatureObjectIds,
                returnGeometry: true,
                gdbVersion: "SDE.DEFAULT",
            });

            for (const key in lightSectorLinesResult) {
                const relatedLines = lightSectorLinesResult[key]?.features;
                if (relatedLines && relatedLines.length > 0) {
                    sectorlineDefaultFeatures.push(...relatedLines);
                }
            }

            for (const key in lightSectorAreaResult) {
                const relatedAreas = lightSectorAreaResult[key]?.features;
                if (relatedAreas && relatedAreas.length > 0) {
                    sectorAreaDefaultFeatures.push(...relatedAreas);
                }
            }

            if (lightSectorFeatures?.features && lightSectorFeatures.features.length > 0) {
                lightSectorDefaultFeatures.push(...lightSectorFeatures.features);
            }
        }
        return {
            featuresToAdd: {
                lightSector: lightSectorDefaultFeatures,
                sectorLine: sectorlineDefaultFeatures,
                sectorArea: sectorAreaDefaultFeatures,
            },
            featuresToDelete: {
                lightSector: lightSectorVersionFeatures,
                sectorLine: sectorlineVersionFeatures,
                sectorArea: sectorAreaVersionFeatures,
            },
        };
    }
    return {
        featuresToAdd: { lightSector: [], sectorLine: [], sectorArea: [] },
        featuresToDelete: { lightSector: [], sectorLine: [], sectorArea: [] },
    };
};

/**
 * Takes in an operative light feature and returns related IB, Racon and Flytendemerke
 * @param feature
 */
export const getLightRelatedOperativeFeatures = async (
    IBFeature?: Graphic,
    raconFeature?: Graphic,
    floatingFeature?: Graphic
): Promise<{ IB: Graphic | null; Racon: Graphic | null; Floating: Graphic | null }> => {
    let relatedIBFeatures: Graphic | null = null;
    let relatedRaconFeatures: Graphic | null = null;
    let relatedFloatingFeatures: Graphic | null = null;

    const promises: Promise<void>[] = [];
    if (IBFeature) {
        promises.push(
            (async () => {
                const operativeIB = await getOperativeFeature(IBFeature, 1);
                if (operativeIB) {
                    relatedIBFeatures = operativeIB;
                }
            })()
        );
    }
    if (raconFeature) {
        promises.push(
            (async () => {
                const operativeRacon = await getOperativeFeature(raconFeature, 4);
                if (operativeRacon) {
                    relatedRaconFeatures = operativeRacon;
                }
            })()
        );
    }
    if (floatingFeature) {
        promises.push(
            (async () => {
                const operativeFloating = await getOperativeFeature(floatingFeature, 6);
                if (operativeFloating) {
                    relatedFloatingFeatures = operativeFloating;
                }
            })()
        );
    }

    await Promise.all(promises);

    return { IB: relatedIBFeatures, Racon: relatedRaconFeatures, Floating: relatedFloatingFeatures };
};

export const getOperativeFeature = async (feature: Graphic, layerId?: number) => {
    let layerIdTemp = layerId;
    layerIdTemp ??= (feature.layer as FeatureLayer)?.layerId ?? undefined;

    if (layerIdTemp === undefined || layerIdTemp === null) {
        console.error("Feature layerId is undefined or null");
        return null;
    }

    // The operative feature server does not have all the layers of the editing feature
    // server, so a layer that is missing there has no operative feature to fetch.
    if (!isOperativeLayerId(layerIdTemp)) {
        return null;
    }

    const operativeFeatureLayer = new FeatureLayer({
        url: `${AppConfig.OperativeObjectsFeatureServer}${layerIdTemp}`,
    });

    const operativeFeatureQuery = operativeFeatureLayer.createQuery();
    operativeFeatureQuery.where = `globalid = '${feature.attributes.globalid}'`;
    operativeFeatureQuery.outFields = ["*"];
    operativeFeatureQuery.returnGeometry = true;
    const operativeFeatureResult = await operativeFeatureLayer.queryFeatures(operativeFeatureQuery);

    if (operativeFeatureResult.features.length === 0) {
        return null;
    }

    return operativeFeatureResult.features[0];
};

/**
 * Deletes the feature if it is an insert in the version.
 * If deleteRelated is true, related features will also be deleted.
 * @param feature
 * @param versionGuid
 * @param deleteRelated
 * @returns
 */
export const deleteIfOnlyInVersion = async (
    feature: Graphic,
    versionGuid: string,
    versionName: string,
    deleteRelated: boolean
): Promise<boolean> => {
    const layerId = (feature.layer as FeatureLayer)?.layerId;
    if (layerId === undefined || layerId === null) {
        console.error("Feature layerId is undefined or null");
        return false;
    }

    const sessionId = getSessionID();
    const differencesObjectIds = await getDifferencesObjectIds(versionGuid, sessionId);

    const featuresToDelete: { layerId: number; objectIds: string[] }[] = [];

    // Find diffs for matching layer
    const diffsForLayer = differencesObjectIds.find((diff) => diff.layerId === layerId);
    if (!diffsForLayer) {
        return false;
    }

    // Check if feature objectid is in inserts list
    const isOnlyInVersion = diffsForLayer.inserts?.includes(feature.attributes.objectid);
    if (isOnlyInVersion) {
        featuresToDelete.push({ layerId: layerId, objectIds: [feature.attributes.objectid] });
    } else {
        toast.info("Objektet finnes i Planroot, kan derfor ikke slettes.");
        return false;
    }

    if (deleteRelated) {
        // Get all related features
        const featureObject: FeatureObject = {
            layerId: layerId,
            type: "insert",
            objectId: feature.attributes.objectid,
            versionGraphic: feature,
        };
        const allRelated = await getAllRelatedFeaturesRecursive([featureObject], versionName, true, true);
        for (const relatedFeature of allRelated) {
            for (const related of relatedFeature.relatedFeatures) {
                // We do not want to delete related overettlinjer, elektroskap or sjøkabel
                const layersToIgnore = [7, 8, 12, 13];
                if (layersToIgnore.includes(related.layerId)) {
                    continue;
                }
                const diffsForLayer = differencesObjectIds.find((diff) => diff.layerId === related.layerId);
                if (!diffsForLayer) {
                    continue;
                }
                const isOnlyInVersion = diffsForLayer.inserts?.includes(related.objectId);
                if (isOnlyInVersion) {
                    // if layer already in featuresToDelete push to objectIds
                    const existingLayer = featuresToDelete.find((f) => f.layerId === related.layerId);
                    if (existingLayer) {
                        existingLayer.objectIds.push(related.objectId);
                    } else {
                        featuresToDelete.push({ layerId: related.layerId, objectIds: [related.objectId] });
                    }
                }
            }
        }
    }

    // Now delete all features in featuresToDelete
    let deleteCounter = 1;
    for (const featureToDelete of featuresToDelete) {
        const layer = new FeatureLayer({
            url: `${AppConfig.NauticalObjectsFeatureServer}${featureToDelete.layerId}`,
        });

        if (!layer.loaded) {
            await layer.load();
        }
        toast.info("Starter sletting av objekt fra versjon...", { toastId: "deleteVersionObject" });

        toast.update("deleteVersionObject", {
            render: `Sletter objekter fra lag ${deleteCounter} av ${featuresToDelete.length} fra versjon...`,
            progress: deleteCounter / featuresToDelete.length,
            autoClose: false,
        });
        deleteCounter++;
        const pseudoFeatures = [];
        for (const objectid of featureToDelete.objectIds) {
            const objectidField = layer.objectIdField;
            const pseudoFeature = new Graphic({
                attributes: {
                    [objectidField]: objectid,
                },
            });
            pseudoFeatures.push(pseudoFeature);
        }
        await applyEdits(layer, { deleteFeatures: pseudoFeatures }, { gdbVersion: versionName });
    }

    return true;
};

export const isMyVersion = (versionInfo: VersionInfo, user: PortalUser): boolean => {
    let isMyVersion = false;
    const versionOwner = getVersionOwner(versionInfo).toLowerCase();
    const userName = user.username.toLowerCase();
    if (versionOwner === userName) {
        isMyVersion = true;
    }
    return isMyVersion;
};

export const isDefaultVersion = (
    versionInfo: VersionInfo | VersionInfoExtended | null,
    versionManagementService: VersionManagementService
): boolean => {
    if (versionInfo === null || !versionManagementService?.defaultVersionIdentifier) {
        return false;
    }
    if (versionInfo.versionIdentifier.guid === versionManagementService.defaultVersionIdentifier.guid) {
        return true;
    }
    return false;
};

export const getVersionName = (versionInfo: VersionInfo | VersionInfoExtended | null): string => {
    if (versionInfo === null) {
        return "Fant ingen versjon";
    }
    let versionName = versionInfo.versionIdentifier.name;
    versionName = versionName.split("").reverse().join("");
    versionName = versionName.slice(0, versionName.indexOf("."));

    return versionName.split("").reverse().join("");
};

export const getVersionOwner = (versionInfo: VersionInfo | VersionInfoExtended | null): string => {
    if (versionInfo === null) {
        return "Fant ingen versjon";
    }
    let versionName = getVersionName(versionInfo);
    let versionOwner = versionInfo.versionIdentifier.name.replace("." + versionName, "");
    return versionOwner;
};

export const canEditVersion = async (
    versionInfo: VersionInfo | VersionInfoExtended | null,
    versionsSharedWithMe: string[] | null,
    user?: PortalUser | null
): Promise<boolean> => {
    if (versionInfo === null || versionsSharedWithMe === null) {
        return false;
    }
    if (user === null || user === undefined) {
        return false;
    }

    const versionOwner = getVersionOwner(versionInfo);
    const userName = user.username;

    if (versionOwner.toLowerCase() === userName.toLowerCase()) {
        return true;
    }

    if (versionsSharedWithMe.includes(versionInfo.versionIdentifier.guid)) {
        return true;
    }

    return false;
};

export const getVersionsSharedWithMe = async (user: PortalUser): Promise<string[]> => {
    const versionSharedWithMe: string[] = [];
    let mapView = getMapView();
    let attemptsMapview = 0;
    while (!mapView && attemptsMapview < 3) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        mapView = getMapView();
        attemptsMapview++;
    }
    if (!mapView?.map) {
        return [];
    }

    let versionAccessTable = mapView.map.findTableById("Versjonstilgang") as FeatureLayer;
    let attempts = 0;
    while (!versionAccessTable && attempts < 3) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        versionAccessTable = mapView.map.findTableById("Versjonstilgang") as FeatureLayer;
        attempts++;
    }
    if (!versionAccessTable) {
        return [];
    }

    const query = versionAccessTable.createQuery();
    query.outFields = ["*"];
    query.where = `epost LIKE '${user.email}'`;

    const result = await versionAccessTable.queryFeatures(query);
    if (result.features.length > 0) {
        result.features.forEach((feature) => {
            versionSharedWithMe.push(feature.attributes.versjon_id);
        });
    }
    return versionSharedWithMe;
};

export const getVersionSharedWith = async (version: VersionInfo | VersionInfoExtended): Promise<Graphic[]> => {
    const versionSharedWith: Graphic[] = [];
    let mapView = getMapView();
    let attemptsMapview = 0;
    while (!mapView && attemptsMapview < 3) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        mapView = getMapView();
        attemptsMapview++;
    }
    if (!mapView?.map) {
        return [];
    }

    let versionAccessTable = mapView.map.findTableById("Versjonstilgang") as FeatureLayer;
    let attempts = 0;
    while (!versionAccessTable && attempts < 3) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        versionAccessTable = mapView.map.findTableById("Versjonstilgang") as FeatureLayer;
        attempts++;
    }
    if (!versionAccessTable) {
        return [];
    }

    const query = versionAccessTable.createQuery();
    query.outFields = ["*"];
    query.where = `versjon_id LIKE '${version.versionIdentifier.guid}'`;

    const result = await versionAccessTable.queryFeatures(query);
    if (result.features.length > 0) {
        return result.features;
    }
    return versionSharedWith;
};

export const acceptVersionAccess = async (
    versionInfo: VersionInfoExtended | null,
    user: PortalUser
): Promise<string[]> => {
    const versionSharedWithMe: string[] = [];
    let mapView = getMapView();
    let attemptsMapview = 0;
    while (!mapView && attemptsMapview < 3) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        mapView = getMapView();
        attemptsMapview++;
    }
    if (!mapView?.map) {
        return [];
    }

    let versionAccessTable = mapView.map.findTableById("Versjonstilgang") as FeatureLayer;
    let attempts = 0;
    while (!versionAccessTable && attempts < 3) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        versionAccessTable = mapView.map.findTableById("Versjonstilgang") as FeatureLayer;
        attempts++;
    }
    if (!versionAccessTable) {
        return [];
    }
    await applyEdits(versionAccessTable, {
        addFeatures: [
            new Graphic({
                attributes: {
                    epost: user.email,
                    versjon_id: versionInfo?.versionIdentifier.guid,
                },
            }),
        ],
    });

    const query = versionAccessTable.createQuery();
    query.outFields = ["*"];
    query.where = `epost LIKE '${user.email}'`;

    const result = await versionAccessTable.queryFeatures(query);
    if (result.features.length > 0) {
        result.features.forEach((feature) => {
            versionSharedWithMe.push(feature.attributes.versjon_id);
        });
    }
    return versionSharedWithMe;
};

export const removeVersionAccess = async (accessVersionGlobalId: string): Promise<boolean> => {
    let removalResult = false;
    let mapView = getMapView();
    let attemptsMapview = 0;
    while (!mapView && attemptsMapview < 3) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        mapView = getMapView();
        attemptsMapview++;
    }
    if (!mapView?.map) {
        return false;
    }

    let versionAccessTable = mapView.map.findTableById("Versjonstilgang") as FeatureLayer;
    let attempts = 0;
    while (!versionAccessTable && attempts < 3) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        versionAccessTable = mapView.map.findTableById("Versjonstilgang") as FeatureLayer;
        attempts++;
    }
    if (!versionAccessTable) {
        return false;
    }

    await applyEdits(
        versionAccessTable,
        {
            deleteFeatures: [{ globalId: accessVersionGlobalId }],
        },
        {
            globalIdUsed: true,
        }
    )
        .then((result) => {
            if (result.deleteFeatureResults[0].error) {
                console.error("Error deleting feature: ", result.deleteFeatureResults[0].error);
                return false;
            } else {
                removalResult = true;
            }
        })
        .catch((error) => {
            console.error(error);
            return false;
        });

    return removalResult;
};

export const purgelocks = async (versionName: string): Promise<boolean> => {
    let success = false;

    const versionServiceUrl = getVersionServiceUrl();
    const token = await getAGEToken();

    const response = await fetch(`${versionServiceUrl}/purgeLock?versionName=${versionName}&f=json`, {
        method: "POST",
        headers: {
            "X-Esri-Authorization": "Bearer " + token,
        },
    });

    if (response.ok) {
        success = true;
    } else {
        console.error("Error purging lock:", response.statusText);
    }

    return success;
};

export const checkIfObjectExistsOnDefault = async (object: Graphic) => {
    const layerId = (object.layer as FeatureLayer).layerId;
    const defaultFeatureLayer = new FeatureLayer({
        url: `${AppConfig.NauticalObjectsFeatureServer}${layerId}`,
    });

    const result = await defaultFeatureLayer.queryFeatureCount({
        objectIds: [object.attributes.objectid],
    });

    if (result > 0) {
        return true;
    }
    return false;
};

export const checkIfInsert = async (object: Graphic, versionGuid: string): Promise<boolean> => {
    const layerId = (object.layer as FeatureLayer).layerId;
    const sessionId = getSessionID();
    const differencesObjectIds = await getDifferencesObjectIds(versionGuid, sessionId);

    // Find diffs for matching layer
    const diffsForLayer = differencesObjectIds.find((diff) => diff.layerId === layerId);
    if (!diffsForLayer) {
        return false;
    }
    // Check if feature objectid is in inserts list
    const isOnlyInVersion = diffsForLayer.inserts?.includes(object.attributes.objectid);
    return isOnlyInVersion ?? false;
};
